import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import {
  cleanBrandName,
  deliveryKeyForBrand,
  formatMoney,
  getPriceSummary,
  headingKeyForBrand,
} from '@/components/brand/brand-content';
import FaqList from '@/components/faq-list';
import { FAQSchema } from '@/components/json-ld';
import { Lead, Section, SectionTitle } from '@/components/ui';
import config from '@/config';
import { countryNameInSentence } from '@/utils/country-name';

// Above this many denominations the answer gives the range instead of the list.
const MAX_AMOUNTS = 12;

const Header = styled.div`
  margin: 0 auto 32px;
  max-width: 768px;
  text-align: center;

  p {
    margin-top: 12px;
  }
`;

const linkStyle = `
  a {
    color: var(--primary);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const Subtitle = styled(Lead)`
  ${linkStyle};
`;

const Questions = styled(FaqList)`
  ${linkStyle};
`;

const HelpLink = (chunks) => (
  <a href={config.supportUrl} rel="noopener noreferrer" target="_blank">
    {chunks}
  </a>
);

const DetailsLink = (chunks) => <a href="#details">{chunks}</a>;

// For the plain-text copy of an answer (JSON-LD): keep the link text, drop the tag.
const plain = (chunks) => chunks;

/**
 * Questions a buyer asks about one product, answered from the brand record
 * (delivery type, country, redemption data, amounts). Returns the visible
 * answer and the plain-text one used for FAQPage JSON-LD, in the same order.
 */
export const useBrandFaqs = (brand) => {
  const t = useTranslations('brand');
  const { locale } = useRouter();
  const product = t('faqProduct', {
    type: headingKeyForBrand(brand),
    brandName: cleanBrandName(brand.name),
  });
  const delivery = deliveryKeyForBrand(brand);
  const methods = (brand.redemptionMethods || []).join(', ');
  const hasInstructions = !!brand.redemptionInstructions || !!brand.redemptionInstructionsUrl;
  const prices = getPriceSummary(brand.products);

  const item = (question, key, values = {}) => ({
    question,
    answer: t.rich(key, { ...values, link: DetailsLink, help: HelpLink }),
    text: t.markup(key, { ...values, link: plain, help: plain }),
  });

  const faqs = [
    item(t('faqDeliveryQuestion', { product }), 'faqDeliveryAnswer', { delivery }),
    item(t('faqWhereQuestion', { product }), 'faqWhereAnswer', {
      country: countryNameInSentence(brand.country, locale),
      hasMethods: methods ? 'yes' : 'no',
      methods,
    }),
    item(t('faqRedeemQuestion', { product }), 'faqRedeemAnswer', {
      mode: hasInstructions ? 'instructions' : delivery,
    }),
  ];

  if (prices) {
    const money = (amountInCents) => formatMoney(amountInCents, prices.currency, locale);
    let kind = 'list';

    if (prices.isRange) {
      kind = 'range';
    } else if (prices.amounts.length === 1) {
      kind = 'single';
    } else if (prices.amounts.length > MAX_AMOUNTS) {
      kind = 'between';
    }

    faqs.push(
      item(t('faqAmountsQuestion'), 'faqAmountsAnswer', {
        kind,
        low: money(prices.lowInCents),
        high: money(prices.highInCents),
        amounts: prices.amounts.map(money).join(', '),
        hasIncrement: prices.increment ? 'yes' : 'no',
        increment: prices.increment ? money(prices.increment * 100) : '',
      })
    );
  }

  faqs.push(
    item(t('faqRefundQuestion', { product }), 'faqRefundAnswer'),
    item(t('faqHelpQuestion'), 'faqHelpAnswer')
  );

  return faqs;
};

/** Brand FAQ, rendered on the server together with its FAQPage JSON-LD. */
const BrandFAQ = ({ brand }) => {
  const t = useTranslations('brand');
  const faqs = useBrandFaqs(brand);

  return (
    <Section id="faq" $spacing="compact">
      <FAQSchema faqs={faqs.map(({ question, text }) => ({ question, answer: text }))} />
      <Header>
        <SectionTitle>{t('faqTitle')}</SectionTitle>
        <Subtitle>{t.rich('faqSubtitle', { link: HelpLink })}</Subtitle>
      </Header>
      <Questions faqs={faqs} />
    </Section>
  );
};

export default BrandFAQ;
